import { COMPANY_CODE_PATTERN, type CompanyBranding } from "@techplanner/shared/core";

/**
 * Société du téléphone : son code est retenu dès l'ouverture du lien d'invitation
 * (/s/<code>), pour que personne n'ait à le taper.
 */

const CODE_KEY = "tp:company-code";
const BRANDING_KEY = "tp:company-branding";

export function normalizeCompanyCode(value: string): string | null {
  const code = value.trim().toLowerCase();
  return COMPANY_CODE_PATTERN.test(code) ? code : null;
}

export function storedCompanyCode(): string | null {
  try {
    return normalizeCompanyCode(localStorage.getItem(CODE_KEY) ?? "");
  } catch {
    return null;
  }
}

export function rememberCompanyCode(code: string): void {
  try {
    if (storedCompanyCode() !== code) localStorage.removeItem(BRANDING_KEY);
    localStorage.setItem(CODE_KEY, code);
  } catch {
    /* stockage indisponible : le code sera redemandé */
  }
}

export function forgetCompany(): void {
  try {
    localStorage.removeItem(CODE_KEY);
    localStorage.removeItem(BRANDING_KEY);
  } catch {
    /* rien à oublier */
  }
}

/** Dernière présentation connue de la société : l'écran de connexion s'affiche à ses couleurs, même hors réseau. */
export function storedBranding(): CompanyBranding | null {
  try {
    const raw = localStorage.getItem(BRANDING_KEY);
    const branding = raw ? (JSON.parse(raw) as CompanyBranding) : null;
    return branding && branding.code === storedCompanyCode() ? branding : null;
  } catch {
    return null;
  }
}

export function rememberBranding(branding: CompanyBranding): void {
  try {
    localStorage.setItem(BRANDING_KEY, JSON.stringify(branding));
  } catch {
    /* non bloquant */
  }
}

/** Lien à envoyer à un technicien : il ouvre l'app sur la bonne société, identifiant prérempli. */
export function invitationLink(code: string, username?: string): string {
  const url = new URL(`/s/${encodeURIComponent(code)}`, window.location.origin);
  if (username) url.searchParams.set("u", username);
  return url.toString();
}

/** Envoie le lien par le partage du téléphone (SMS, WhatsApp…), sinon le copie. */
export async function shareInvitation(link: string, companyName: string, who?: string): Promise<"shared" | "copied" | "cancelled"> {
  const text = `${who ? `Bonjour ${who}, voici` : "Voici"} le lien pour ouvrir l'application ${companyName} sur votre téléphone :`;
  if (navigator.share) {
    try {
      await navigator.share({ title: companyName, text, url: link });
      return "shared";
    } catch (err) {
      if ((err as Error).name === "AbortError") return "cancelled";
    }
  }
  await navigator.clipboard.writeText(`${text} ${link}`);
  return "copied";
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Texte lisible (blanc ou presque noir) posé sur la couleur de la société. */
function inkFor(hex: string): string {
  const [r, g, b] = hexToRgb(hex).map((c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.4 ? "#0f1b2d" : "#ffffff";
}

/**
 * Application installée aux couleurs de la société : le téléphone lit le manifeste
 * (nom sous l'icône, icône, couleur) au moment de l'ajout à l'écran d'accueil.
 * Sans société connue, on revient à celui de TechPlanner.
 */
export function applyCompanyApp(branding: Pick<CompanyBranding, "code" | "name" | "appIconUrl"> | null): void {
  // Le lien du manifeste n'est ajouté qu'à la compilation : on le crée s'il manque (développement).
  let manifest = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!manifest) {
    manifest = Object.assign(document.createElement("link"), { rel: "manifest" });
    document.head.appendChild(manifest);
  }
  const touchIcon = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
  const appTitle = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
  if (branding) {
    manifest?.setAttribute("href", `/api/public/companies/${encodeURIComponent(branding.code)}/manifest.webmanifest`);
    touchIcon?.setAttribute("href", branding.appIconUrl ?? "/icons/apple-touch-icon.png");
    appTitle?.setAttribute("content", branding.name);
    document.title = branding.name;
  } else {
    manifest?.setAttribute("href", "/manifest.webmanifest");
    touchIcon?.setAttribute("href", "/icons/apple-touch-icon.png");
    appTitle?.setAttribute("content", "TechPlanner");
    document.title = "TechPlanner";
  }
}

async function loadImage(url: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.src = url;
  await img.decode();
  return img;
}

/** Même limite que le serveur (routes/company.ts). */
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

/**
 * Logo prêt à envoyer : les bons PDF (pdfkit) ne lisent que le JPEG et le PNG,
 * donc un WebP est redessiné en PNG dans le navigateur. Les autres formats partent tels quels.
 * Le poids est vérifié ici, après conversion, pour un message juste (2 Mo).
 */
export async function logoForUpload(file: File): Promise<File> {
  const ready = await convertedLogo(file);
  if (ready.size > MAX_LOGO_BYTES) {
    throw new Error(
      file.type === "image/webp"
        ? "Logo trop lourd une fois converti en PNG (2 Mo maximum) : réduisez-le ou envoyez-le en JPEG."
        : "Logo trop lourd (2 Mo maximum)."
    );
  }
  return ready;
}

async function convertedLogo(file: File): Promise<File> {
  if (file.type !== "image/webp") return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Conversion du logo impossible sur cet appareil : envoyez-le en PNG ou en JPEG.");
    ctx.drawImage(img, 0, 0);
    const png = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Conversion du logo impossible : envoyez-le en PNG ou en JPEG."))), "image/png")
    );
    return new File([png], `${file.name.replace(/\.webp$/i, "") || "logo"}.png`, { type: "image/png" });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Dessine l'icône de l'app : le logo sur fond blanc, ou l'initiale sur la couleur de la société. */
async function drawAppIcon(size: number, branding: Pick<CompanyBranding, "name" | "primaryColor" | "logoUrl">): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Dessin impossible sur cet appareil.");
  const color = branding.primaryColor && /^#[0-9a-f]{6}$/i.test(branding.primaryColor) ? branding.primaryColor : "#0b5d7a";

  if (branding.logoUrl) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, size, size);
    const logo = await loadImage(branding.logoUrl);
    // Zone sûre des icônes « masquables » : le logo tient dans 70 % de la largeur.
    const box = size * 0.7;
    const scale = Math.min(box / logo.naturalWidth, box / logo.naturalHeight);
    const w = logo.naturalWidth * scale;
    const h = logo.naturalHeight * scale;
    ctx.drawImage(logo, (size - w) / 2, (size - h) / 2, w, h);
  } else {
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = inkFor(color);
    ctx.font = `700 ${Math.round(size * 0.5)}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(branding.name.trim().charAt(0).toUpperCase() || "T", size / 2, size / 2 + size * 0.03);
  }
  return new Promise((resolve, reject) => canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Icône vide."))), "image/png"));
}

/** Génère les icônes de l'app installée et les envoie au serveur. */
export async function buildAppIcons(branding: Pick<CompanyBranding, "name" | "primaryColor" | "logoUrl">): Promise<FormData> {
  const form = new FormData();
  form.append("icon192", await drawAppIcon(192, branding), "icon-192.png");
  form.append("icon512", await drawAppIcon(512, branding), "icon-512.png");
  return form;
}

const THEME_STYLE_ID = "company-theme";

/** Applique la couleur de la société à toute l'application (clair et sombre). Sans couleur : thème TechPlanner. */
export function applyCompanyColor(color: string | null): void {
  const existing = document.getElementById(THEME_STYLE_ID);
  if (!color || !/^#[0-9a-f]{6}$/i.test(color)) {
    existing?.remove();
    return;
  }
  const light = `--brand: ${color}; --brand-strong: color-mix(in srgb, ${color} 80%, #000); --brand-soft: color-mix(in srgb, ${color} 12%, #fff); --brand-ink: ${inkFor(color)};`;
  const darkBrand = `color-mix(in srgb, ${color} 70%, #fff)`;
  const dark = `--brand: ${darkBrand}; --brand-strong: color-mix(in srgb, ${color} 55%, #fff); --brand-soft: color-mix(in srgb, ${color} 22%, #0b1220); --brand-ink: #04141b;`;
  const css = `:root { ${light} }
@media (prefers-color-scheme: dark) { :root:not([data-theme="light"]) { ${dark} } }
:root[data-theme="dark"] { ${dark} }`;
  const style = existing ?? Object.assign(document.createElement("style"), { id: THEME_STYLE_ID });
  style.textContent = css;
  if (!existing) document.head.appendChild(style);
  document.querySelector('meta[name="theme-color"][media*="light"]')?.setAttribute("content", color);
}
